import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, GripVertical, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

function FAQRow({ item, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <GripVertical size={16} className="text-slate-300 shrink-0" />
        <span className="w-6 h-6 rounded-full bg-[#2D6CDF]/10 text-[#2D6CDF] text-xs font-bold flex items-center justify-center shrink-0">{index + 1}</span>
        <p className={`flex-1 text-sm font-medium ${item.question ? 'text-slate-800' : 'text-slate-400 italic'}`}>
          {item.question || 'Pertanyaan belum diisi...'}
        </p>
        <div className="flex items-center gap-1 ml-auto" onClick={e => e.stopPropagation()}>
          <Switch checked={item.is_visible} onCheckedChange={v => onChange({ ...item, is_visible: v })} />
          <Button variant="ghost" size="icon" onClick={() => onMoveUp()} disabled={isFirst}><ChevronUp size={15} /></Button>
          <Button variant="ghost" size="icon" onClick={() => onMoveDown()} disabled={isLast}><ChevronDown size={15} /></Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <Trash2 size={15} className="text-red-400" />
          </Button>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400 shrink-0" /> : <ChevronDown size={16} className="text-slate-400 shrink-0" />}
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 bg-slate-50/50">
          <div className="pt-3">
            <Label className="text-xs">Pertanyaan</Label>
            <Input
              value={item.question}
              onChange={e => onChange({ ...item, question: e.target.value })}
              placeholder="Masukkan pertanyaan..."
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Jawaban</Label>
            <Textarea
              value={item.answer}
              onChange={e => onChange({ ...item, answer: e.target.value })}
              rows={4}
              placeholder="Masukkan jawaban..."
              className="mt-1"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function TenantFAQ() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // index of open preview item

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tenant/faq');
      setItems(res.data.items || []);
    } catch { toast.error('Gagal memuat FAQ'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const addItem = () => {
    setItems(it => [...it, {
      id: `new-${Date.now()}`,
      question: '',
      answer: '',
      order: it.length,
      is_visible: true,
    }]);
  };

  const updateItem = (index, updated) => {
    setItems(it => it.map((item, i) => i === index ? updated : item));
  };

  const removeItem = (index) => {
    setItems(it => it.filter((_, i) => i !== index));
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setItems(it => {
      const next = [...it];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((item, i) => ({ ...item, order: i }));
    });
  };

  const moveDown = (index) => {
    setItems(it => {
      if (index === it.length - 1) return it;
      const next = [...it];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((item, i) => ({ ...item, order: i }));
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map((item, i) => ({ ...item, order: i }));
      await api.put('/tenant/faq', { items: payload });
      toast.success('FAQ berhasil disimpan');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
    </div>
  );

  const visibleCount = items.filter(i => i.is_visible).length;

  return (
    <div className="space-y-6" data-testid="tenant-faq-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>FAQ</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} pertanyaan • {visibleCount} tampil di toko</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={addItem}><Plus size={16} className="mr-2" /> Tambah FAQ</Button>
          <Button onClick={save} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
            {saving ? 'Menyimpan...' : 'Simpan Semua'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* FAQ List */}
        <div className="lg:col-span-2 space-y-3">
          {items.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="py-16 text-center text-slate-400">
                <HelpCircle size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">Belum ada FAQ</p>
                <p className="text-sm mt-1">Tambahkan pertanyaan yang sering ditanyakan pelanggan</p>
                <Button variant="link" onClick={addItem} className="mt-3">+ Tambah FAQ pertama</Button>
              </CardContent>
            </Card>
          ) : items.map((item, index) => (
            <FAQRow
              key={item.id}
              item={item}
              index={index}
              onChange={(updated) => updateItem(index, updated)}
              onRemove={() => removeItem(index)}
              onMoveUp={() => moveUp(index)}
              onMoveDown={() => moveDown(index)}
              isFirst={index === 0}
              isLast={index === items.length - 1}
            />
          ))}
          {items.length > 0 && (
            <Button variant="outline" onClick={addItem} className="w-full border-dashed">
              <Plus size={16} className="mr-2" /> Tambah Pertanyaan
            </Button>
          )}
        </div>

        {/* Preview panel */}
        <div className="space-y-4">
          <Card className="border-slate-200 shadow-sm sticky top-4">
            <CardHeader>
              <CardTitle className="text-base">Preview Tampilan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {items.filter(i => i.is_visible && i.question).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Belum ada FAQ yang aktif</p>
              ) : items.filter(i => i.is_visible && i.question).map((item, i) => (
                <div key={item.id} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-800 hover:bg-slate-50 text-left"
                    onClick={() => setPreview(preview === i ? null : i)}
                  >
                    {item.question}
                    {preview === i ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  {preview === i && item.answer && (
                    <div className="px-4 pb-3 text-sm text-slate-600 border-t border-slate-100 bg-slate-50/50 whitespace-pre-wrap">
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
